import { ActionType, createReducer } from 'typesafe-actions'

import { QuarterFilter } from 'src/types'

import * as Actions from './actions'
import { HotelSearchPeriods, HotelSearchType, HotelsState } from './types'

const INITIAL_STATE: HotelsState = {
  currentPeriodKey: null,
  historyError: '',
  historyList: null,
  historyLoading: null,
  searches: {},
  searchType: HotelSearchType.SINGLE_PERIOD
}

const reducer = createReducer<HotelsState, ActionType<typeof Actions>>(
  INITIAL_STATE
)
  .handleAction(Actions.getHotels, (state, { payload }) => ({
    ...state,
    ...(payload.searchType === HotelSearchType.SINGLE_PERIOD
      ? {
          searches: {
            [payload.searchKey]: {
              error: '',
              filters: payload.filters,
              list: [],
              loading: true,
              message_id: [],
              selectionFilters: null
            }
          },
          searchType: HotelSearchType.SINGLE_PERIOD
        }
      : {
          searchType: HotelSearchType.MULTIPLE_PERIOD,
          searches: {
            ...(payload.searchType !== state.searchType ? {} : state.searches),
            [payload.searchKey]: {
              error: '',
              filters: payload.filters,
              list: [],
              loading: true,
              message_id: [],
              selectionFilters: null
            }
          }
        })
  }))
  .handleAction(Actions.getHotelsFailure, (state, { payload }) => {
    const searches: HotelSearchPeriods = {
      ...state.searches,
      [payload.searchKey]: {
        ...state.searches[payload.searchKey],
        list: [],
        loading: false,
        error: payload.message,
        message_id: [],
        selectionFilters: {
          name: '',
          quarter: []
        }
      }
    }

    const hasFinishedLoading = Object.values(searches).every(
      ({ loading }) => !loading
    )

    if (!hasFinishedLoading) {
      return {
        ...state,
        searches,
        currentPeriodKey: ''
      }
    }

    const sortSearches = Object.entries(searches)
      .sort()
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

    return {
      ...state,
      searches: sortSearches,
      currentPeriodKey: Object.keys(sortSearches)[0]
    }
  })
  .handleAction(Actions.getHotelsSuccess, (state, { payload }) => {
    const searches: HotelSearchPeriods = {
      ...state.searches,
      [payload.searchKey]: {
        ...state.searches[payload.searchKey],
        list: [...payload.search],
        loading: false,
        message_id: [...payload.message_id],
        selectionFilters: {
          name: '',
          quarter: [
            ...new Set(
              Object.values(payload.search).map<string>(
                hotelItem => hotelItem.quarter
              )
            )
          ]
            .filter(quarter => quarter)
            .sort()
            .map<QuarterFilter>(quarter => ({
              checked: false,
              qtd: 0,
              title: quarter
            }))
        }
      }
    }
    const hasFinishedLoading = Object.values(searches).every(
      ({ loading }) => !loading
    )

    if (!hasFinishedLoading) {
      return {
        ...state,
        searches,
        currentPeriodKey: ''
      }
    }

    const sortSearches = Object.entries(searches)
      .sort()
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})

    return {
      ...state,
      searches: sortSearches,
      currentPeriodKey: Object.keys(sortSearches)[0]
    }
  })
  .handleAction(Actions.getHotelsHistory, state => ({
    ...state,
    historyError: '',
    historyList: null,
    historyLoading: true
  }))
  .handleAction(Actions.getHotelsHistoryFailure, (state, { payload }) => ({
    ...state,
    historyError: payload.message,
    historyLoading: false
  }))
  .handleAction(Actions.getHotelsHistorySuccess, (state, { payload }) => ({
    ...state,
    historyLoading: false,
    historyList: payload.historyList
  }))
  .handleAction(Actions.setHotelsNextPeriodKey, state => {
    const periodCount = Object.keys(state.searches)
    const currentIndex = periodCount.findIndex(
      index => index === state.currentPeriodKey
    )
    return {
      ...state,
      ...(currentIndex < periodCount.length - 1
        ? { currentPeriodKey: periodCount[currentIndex + 1] }
        : {})
    }
  })
  .handleAction(Actions.setHotelsPeriodKey, (state, { payload }) => ({
    ...state,
    currentPeriodKey: payload.periodKey
  }))
  .handleAction(Actions.setHotelsFilterQuarter, (state, { payload }) => ({
    ...state,
    searches: {
      ...state.searches,
      [state.currentPeriodKey]: {
        ...state.searches[state.currentPeriodKey],
        selectionFilters: {
          ...state.searches[state.currentPeriodKey].selectionFilters,
          quarter: state.searches[
            state.currentPeriodKey
          ].selectionFilters.quarter.map((item, index) =>
            index === payload.index ? { ...item, checked: !item.checked } : item
          )
        }
      }
    }
  }))
  .handleAction(Actions.setHotelsFilterQuarterAll, (state, { payload }) => ({
    ...state,
    searches: {
      ...state.searches,
      [state.currentPeriodKey]: {
        ...state.searches[state.currentPeriodKey],
        selectionFilters: {
          ...state.searches[state.currentPeriodKey].selectionFilters,
          quarter: state.searches[
            state.currentPeriodKey
          ].selectionFilters.quarter.map(item => ({
            ...item,
            checked: payload.checked
          }))
        }
      }
    }
  }))
  .handleAction(Actions.setHotelsFilterName, (state, { payload }) => {
    if (!state.currentPeriodKey) {
      return state
    }
    return {
      ...state,
      searches: {
        ...state.searches,
        [state.currentPeriodKey]: {
          ...state.searches[state.currentPeriodKey],
          selectionFilters: {
            ...state.searches[state.currentPeriodKey]?.selectionFilters,
            name: payload.value
          }
        }
      }
    }
  })
  .handleAction(Actions.resetHotels, state => ({
    currentPeriodKey: null,
    historyError: '',
    historyList: null,
    historyLoading: null,
    searches: {},
    searchType: state.searchType
  }))

export default reducer
